import { FormsInputDropdown, InputDropdownItem, FormsCheckBox } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useState } from 'react';
import { AdminProfessionals } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { GetAdminProfessionalsParams } from '@platform/client/schemas';

type UpdateCohortFormData = {
  clientId: number | null;
  cohortId: number | null;
  isDrop: boolean | null;
};

export const UpdateCohortModal = (props: {
  onClose: () => void;
  userId: number;
  currentCohort: string;
  currentPartner: string;
  proName: string;
  queryKeyToInvalidate?: readonly (string | GetAdminProfessionalsParams)[];
}) => {
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, handleSubmit, watch, formState, setValue } = useForm<UpdateCohortFormData>({
    defaultValues: {
      clientId: null,
      cohortId: null,
      isDrop: false,
    },
  });
  const selectedClientId = watch('clientId');

  // Manage dropdown items, each partner has their own cohorts
  const { data: cohortsByPartner } = AdminProfessionals.useGetCohortsByClients();
  const selectedPartner = cohortsByPartner?.clients.find((client) => client.id == selectedClientId);
  const cohortDropdownItems: InputDropdownItem[] =
    selectedPartner ? mapToDropdownItems(selectedPartner.cohorts) : [];

  cohortDropdownItems.unshift({
    textValue: -1,
    text: 'None',
  });
  if (selectedPartner?.name === 'TRANSFORM') {
    setValue('cohortId', null);
  }

  const queryClient = useQueryClient();
  const updateCohortMutator = AdminProfessionals.useUpdateCohort({
    mutation: {
      onMutate: () => {
        setModalStage('submitting');
      },
      onSuccess: () => {
        setModalStage('success');
        if (props.queryKeyToInvalidate) {
          queryClient.invalidateQueries({ queryKey: props.queryKeyToInvalidate });
        }
      },
    },
  });

  const onSubmit: SubmitHandler<UpdateCohortFormData> = (data) => {
    if (data.clientId) {
      updateCohortMutator.mutate({
        data: {
          userId: props.userId,
          clientId: data.clientId,
          cohortId: data.cohortId,
          isDrop: data.isDrop,
        },
      });
    }
  };

  return (
    <AdminFormModal
      modalStage={modalStage}
      title={`Cohort Update: ${props.proName}`}
      onClose={props.onClose}
      submitCTA={{
        buttonVariant: 'primary',
        text: 'Update Cohort',
        callback: () => handleSubmit(onSubmit)(),
      }}
    >
      <div className='flex flex-col'>
        <p className='mb-2'>
          <span className='font-semibold'>Current Partner:</span> {props.currentPartner}
        </p>
        <p className='mb-4'>
          <span className='font-semibold'>Current Cohort:</span> {props.currentCohort}
        </p>
        <FormsInputDropdown
          type='default'
          inputDisabled
          control={control}
          name='clientId'
          label='New Corporate Partner'
          items={mapToDropdownItems(cohortsByPartner?.clients || [])}
          placeholder='Select a partner'
          rules={{ required: 'Partner selection is required' }}
          error={formState.errors.clientId}
        />
        {selectedPartner?.name !== 'TRANSFORM' && (
          <FormsInputDropdown
            type='default'
            inputDisabled
            control={control}
            name='cohortId'
            label='New Cohort'
            items={cohortDropdownItems}
            placeholder='Select a cohort'
            rules={{ required: 'Cohort selection is required' }}
            error={formState.errors.cohortId}
          />
        )}
        {props.currentCohort && (
          <>
            <div>
              <FormsCheckBox control={control} name='isDrop' description='Record drop' size='md' />
            </div>
          </>
        )}
      </div>
    </AdminFormModal>
  );
};

const mapToDropdownItems = (items: { id: number; name: string }[]): InputDropdownItem[] => {
  return items.map((item) => {
    return {
      textValue: item.id,
      text: item.name,
    };
  });
};
